import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Minus, Plus, ShieldCheck } from "lucide-react";
import { INSTRUMENTS } from "@/data/brokers";
import { BrokerMark, Eyebrow, Freshness, Tag } from "@/components/shared/Primitives";
import { useCompare } from "@/context/CompareContext";
import { useBrokers } from "@/context/BrokersContext";
import { Reveal, Stagger, Item } from "@/components/motion/Reveal";
import { money, timeAgo } from "@/lib/format";
import NotFound from "@/pages/NotFound";

const Stat = ({ label, value, sub }) => (
  <div className="border-l border-[var(--line)] pl-4">
    <p className="eyebrow">{label}</p>
    <p className="mt-1.5 t-stat-sm">{value}</p>
    {sub && <p className="font-mono text-[11px] text-[var(--ink-3)]">{sub}</p>}
  </div>
);

const Section = ({ n, title, children }) => (
  <Item className="grid grid-cols-12 gap-6 border-t border-[var(--line)] py-10">
    <div className="col-span-12 lg:col-span-4"><p className="eyebrow">{n}</p><h2 className="mt-2 t-h4">{title}</h2></div>
    <div className="col-span-12 lg:col-span-8">{children}</div>
  </Item>
);

export default function BrokerProfile() {
  const { slug } = useParams();
  const { getBroker } = useBrokers();
  const b = getBroker(slug);
  const { toggle, has } = useCompare();
  if (!b) return <NotFound />;
  const on = has(b.slug);

  return (
    <div data-testid="broker-profile-page">
      <div className="bg-white border-b border-[var(--line)]">
        <div className="container-x py-12 lg:py-16">
          <Link to="/brokers" className="link-arrow text-[var(--ink-3)] mb-8"><ArrowLeft size={14} /> Directory</Link>
          <Reveal className="mt-6 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex items-start gap-5">
              <BrokerMark broker={b} size={72} className="!rounded-2xl" />
              <div>
                <h1 className="t-h1">{b.name}</h1>
                <p className="mt-3 max-w-xl t-lead">{b.tagline}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">{b.entities.map((e) => <Tag key={e.name}>{e.regulator} · {e.jurisdiction}</Tag>)}<Tag tone="blue">Trust {b.trust.toFixed(1)}/10</Tag></div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggle(b.slug, b.short)} data-on={on} className="pill" data-testid="profile-compare-toggle">{on ? <Check size={14} /> : <Plus size={14} />}{on ? "In compare" : "Add to compare"}</button>
              <a href={b.referral} target="_blank" rel="noreferrer sponsored" className="btn-primary" data-testid="profile-visit-broker">Visit broker <ArrowUpRight size={16} /></a>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-5">
            <Stat label="EUR/USD spread" value={`${b.eurUsdSpread.toFixed(1)} pips`} sub={b.commission ? `+ $${b.commission.toFixed(2)}/lot` : "no commission"} />
            <Stat label="Min deposit" value={`$${b.minDeposit}`} />
            <Stat label="Execution" value={`${b.executionMs}ms`} sub="median, London VPS" />
            <Stat label="Withdrawal" value={b.withdrawalTime.split(" (")[0]} sub={b.withdrawalFee} />
            <Stat label="Founded" value={b.founded} sub={`${2026 - b.founded} yrs operating`} />
          </Reveal>
        </div>
      </div>

      <Stagger className="container-x py-6" gap={0.05}>
        <Section n="01 · Overview" title="Legal entities">
          <div className="space-y-3">
            {b.entities.map((e) => (
              <div key={e.name} className="card p-5 grid grid-cols-12 gap-4 items-start" data-testid={`entity-${e.regulator}`}>
                <div className="col-span-12 sm:col-span-5"><p className="font-medium">{e.name}</p><p className="font-mono text-[11px] text-[var(--ink-3)] mt-1">{e.regulator} · {e.ref}</p></div>
                <div className="col-span-6 sm:col-span-3"><p className="eyebrow">Max leverage</p><p className="font-mono text-sm mt-1">{e.leverage}</p></div>
                <div className="col-span-6 sm:col-span-4"><p className="eyebrow">Serves</p><p className="text-xs text-[var(--ink-2)] mt-1 leading-relaxed">{e.regions.join(", ")}</p></div>
              </div>
            ))}
          </div>
        </Section>
        <Section n="02 · Regulation" title="Supervision & protection">
          <div className="flex items-start gap-3 text-sm leading-relaxed text-[var(--ink-2)]"><ShieldCheck size={18} className="text-[var(--pos)] shrink-0 mt-0.5" /><p>Regulatory references were cross-checked against public registers on 10 Jun 2026. Client-money segregation and negative balance protection apply under the {b.entities.filter((e) => ["FCA", "ASIC", "FINMA", "MAS", "CySEC"].includes(e.regulator)).map((e) => e.regulator).join(", ") || "listed"} entities; offshore entities offer higher leverage with reduced investor protection.</p></div>
        </Section>
        <Section n="03 · Accounts" title="Account types & platforms">
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div><p className="eyebrow mb-2">Pricing model</p><p>{b.spreadModel}</p></div>
            <div><p className="eyebrow mb-2">Platforms</p><div className="flex flex-wrap gap-1.5">{b.platforms.map((p) => <Tag key={p}>{p}</Tag>)}</div></div>
            <div><p className="eyebrow mb-2">Swap-free</p><p>{b.swapFree ? b.swapFreeNote : "Not offered"}</p></div>
          </div>
        </Section>
        <Section n="04 · Costs" title="Spreads, commission & swaps">
          <div className="card card-plain overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--line)]"><p className="text-sm font-medium">Published swaps · per 1.00 lot · USD</p><div className="flex items-center gap-3"><Freshness source={b.swapSource} /><span className="font-mono text-[11px] text-[var(--ink-4)]">{timeAgo(b.swapUpdated)}</span></div></div>
            <div className="grid grid-cols-3 px-5 py-2 font-mono text-[11px] tracking-[0.01em] text-[var(--ink-4)] border-b border-[var(--line)]"><span>Instrument</span><span className="text-right">Long</span><span className="text-right">Short</span></div>
            {INSTRUMENTS.map((i) => { const s = b.swaps[i.id]; return (
              <div key={i.id} className="grid grid-cols-3 px-5 py-2.5 border-b border-[var(--line)] last:border-0 text-sm"><span>{i.label}</span><span className={`text-right font-mono ${s.long > 0 ? "text-[var(--pos)]" : ""}`}>{b.swapSource === "Unavailable" ? "—" : money(s.long)}</span><span className={`text-right font-mono ${s.short > 0 ? "text-[var(--pos)]" : ""}`}>{b.swapSource === "Unavailable" ? "—" : money(s.short)}</span></div>
            ); })}
          </div>
        </Section>
        <Section n="05 · Promotions" title="Available promotions">
          <p className="text-sm text-[var(--ink-2)] leading-relaxed">{b.depositBonus || "This broker does not offer deposit promotions under any entity."}</p>
          {b.depositBonus && <p className="mt-2 text-xs text-[var(--ink-4)]">Eligibility is entity-specific; residents under FCA, ASIC, CySEC, FINMA or MAS supervision are generally excluded from bonus offers.</p>}
        </Section>
        <Section n="06 · Funding" title="Deposits & withdrawals">
          <div className="grid grid-cols-2 gap-6 text-sm"><div><p className="eyebrow mb-2">Methods</p><div className="flex flex-wrap gap-1.5">{b.funding.map((f) => <Tag key={f}>{f}</Tag>)}</div></div><div><p className="eyebrow mb-2">Withdrawal speed</p><p>{b.withdrawalTime}</p><p className="text-[var(--ink-3)] mt-1">Fees: {b.withdrawalFee}</p></div></div>
        </Section>
        <Section n="07 · Assessment" title="Advantages, disadvantages & limits">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 text-sm">
            <div><p className="eyebrow mb-3 text-[var(--pos)]">Advantages</p><ul className="space-y-2">{b.pros.map((p) => <li key={p} className="flex gap-2"><Plus size={14} className="shrink-0 mt-0.5 text-[var(--pos)]" />{p}</li>)}</ul></div>
            <div><p className="eyebrow mb-3 text-[var(--neg)]">Disadvantages</p><ul className="space-y-2">{b.cons.map((p) => <li key={p} className="flex gap-2"><Minus size={14} className="shrink-0 mt-0.5 text-[var(--neg)]" />{p}</li>)}</ul></div>
            <div><p className="eyebrow mb-3">Data limitations</p><ul className="space-y-2 text-[var(--ink-3)]">{b.limitations.map((p) => <li key={p}>{p}</li>)}</ul></div>
          </div>
        </Section>
        <Section n="08 · Actions" title="Compare or register">
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/brokers/compare" className="btn-outline" data-testid="profile-go-compare">Compare side by side</Link>
            <Link to="/swaps" className="btn-ghost">Analyse swap costs</Link>
            <a href={b.referral} target="_blank" rel="noreferrer sponsored" className="btn-primary" data-testid="profile-visit-broker-bottom">Open account at {b.short} <ArrowUpRight size={16} /></a>
          </div>
          <p className="mt-4 text-xs text-[var(--ink-4)]">External referral link. We may receive a commission; this never influences scores or rankings.</p>
        </Section>
      </Stagger>
    </div>
  );
}
